import { useSearchLicensesQuery } from '@/api/ariadne/__generated_types__/apollo';
import { NAV_ITEMS } from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import * as dataSelectors from '@/data/data-selectors';
import * as userSelectors from '@/user/user-selectors';
import { MAX_RESULTS } from '@/workers/search-worker/search-worker-utils';
import { workerCommands } from '@/workers/search-worker/worker-constants';
import debounce from 'lodash/debounce';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { queryToResults } from './license-search-transform';
import { mergeSearchResults } from './merge-results';
import { SearchInput } from './search-input';
import { SearchResult, SearchResults } from './search-results';
import { SearchResultsWrapper } from './search-styles';
import { CloseButton, Header, StyledDrawer } from './universal-search-styles';

interface Props {
  isUniversalSearchActive: unknown;
}

export function UniversalSearchDrawer({ isUniversalSearchActive }: Props) {
  const dispatch = useDispatch();

  const [searchParam, setSearchParam] = useState('');
  const [propertySearchResults, setSearchResults] = useState<
    SearchResult[] | null
  >(null);
  const swRef = useRef<Worker>();

  const currentCity = useSelector(userSelectors.currentCity);
  const propertyList = useSelector(dataSelectors.propertyList);

  // Note no debouncing here; if it's required, it should be implemented on the Apollo link (or other transporm library
  // client) level.
  const licenseSearch = useSearchLicensesQuery({
    variables: {
      regionId: currentCity,
      searchTerm: searchParam,
    },
  });

  // Create background search worker.
  useEffect(() => {
    if (!swRef.current) {
      swRef.current = new Worker(
        new URL('../../workers/search-worker/search-worker.js', import.meta.url)
      );

      swRef.current.onmessage = ({ data }) => {
        // This is non-typesafe assignment; we're relying on workers to comply with SearchResult format without actually
        // enforcing it.
        setSearchResults(data.payload);
      };
    }

    swRef.current.postMessage({
      cmd: workerCommands.SET_PROPERTY_LIST,
      payload: { propertyList },
    });

    return () => {
      if (!swRef.current) {
        return;
      }
      swRef.current.postMessage({ cmd: workerCommands.CLOSE });
      swRef.current = undefined;
    };
  }, [propertyList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawerOnClose = () => {
    dispatch(
      dashboardActions.toggleUniversalSearch({ isUniversalSearchActive: false })
    );
    setSearchResults(null);
  };

  const performInMemoryPropertySearch = useMemo(() => {
    return debounce(
      (searchParam: string) => {
        if (!swRef.current) {
          return () => null;
        }

        swRef.current.postMessage({
          cmd: workerCommands.SEARCH,
          payload: { searchParam },
        });
      },
      400,
      { leading: true }
    );
  }, [swRef]);

  const handleSearchParamChange = useCallback(
    (value: string) => {
      setSearchParam(value);

      if (!value) {
        setSearchResults(null);
        return;
      }

      performInMemoryPropertySearch(value);
    },
    [performInMemoryPropertySearch, setSearchParam, setSearchResults]
  );

  const handleDeckardIdSelected = useCallback((value: string) => {
    batch(() => {
      dispatch(
        dashboardActions.openPropertyByDeckardId([
          NAV_ITEMS.home,
          String(value),
        ])
      );
      dispatch(
        dashboardActions.toggleUniversalSearch({
          isUniversalSearchActive: false,
        })
      );
    });

    setSearchResults(null);
    setSearchParam('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergedSearchResults = useMemo(
    function (): SearchResult[] {
      return mergeSearchResults(
        MAX_RESULTS,
        queryToResults(licenseSearch),
        propertySearchResults ?? []
      );
    },
    [propertySearchResults, licenseSearch]
  );

  return (
    <Suspense fallback={<div>Loading</div>}>
      <StyledDrawer
        size="full"
        placement="left"
        open={!!isUniversalSearchActive}
        onClose={handleDrawerOnClose}
      >
        <Header>
          <CloseButton onClick={handleDrawerOnClose} />
        </Header>
        <SearchInput onChange={handleSearchParamChange} />
        <SearchResultsWrapper data-cy="universal-search-results-container">
          {propertySearchResults && (
            <SearchResults
              searchParam={searchParam}
              searchResults={mergedSearchResults}
              handleResultOnClick={handleDeckardIdSelected}
            />
          )}
        </SearchResultsWrapper>
      </StyledDrawer>
    </Suspense>
  );
}
